/**
 * Hover tip for collapsed nav-rail controls (the library Tooltip / Label).
 * Portaled + fixed so it escapes the rail's overflow:hidden.
 */
import { useCallback, useEffect, useState, type FocusEvent, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useCoShellPortal } from "../CoShellPortalContext";

type TipState = { text: string; top: number; left: number };

type TipHandlers = {
  onMouseEnter: (e: MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  onFocus: (e: FocusEvent<HTMLElement>) => void;
  onBlur: () => void;
};

export function useNavRailTip(enabled: boolean): {
  bindTip: (text: string) => Partial<TipHandlers>;
  tipPortal: ReactNode;
} {
  const portalContainer = useCoShellPortal();
  const [tip, setTip] = useState<TipState | null>(null);

  const hide = useCallback(() => setTip(null), []);

  // Expanding the rail unbinds tip handlers, so a visible tip never gets
  // mouseleave/blur — clear it explicitly when tips are disabled.
  useEffect(() => {
    if (!enabled) setTip(null);
  }, [enabled]);

  const show = useCallback((text: string, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setTip({ text, top: r.top + r.height / 2, left: r.right + 8 });
  }, []);

  const bindTip = useCallback(
    (text: string) => {
      if (!enabled) return {};
      return {
        onMouseEnter: (e: MouseEvent<HTMLElement>) => show(text, e.currentTarget),
        onMouseLeave: hide,
        onFocus: (e: FocusEvent<HTMLElement>) => show(text, e.currentTarget),
        onBlur: hide,
      };
    },
    [enabled, hide, show],
  );

  const tipPortal =
    tip &&
    portalContainer &&
    createPortal(
      <span
        role="tooltip"
        style={{
          position: "fixed",
          top: tip.top,
          left: tip.left,
          transform: "translateY(-50%)",
          // Above the rail itself (--z-index-drawer), not just the page.
          zIndex: "var(--z-index-tooltip)",
          pointerEvents: "none",
          whiteSpace: "nowrap",
          /* Tooltip / Label (library, style B): the solid inverse plane at
             radius 8, so its small text reads over anything. */
          borderRadius: 8,
          background: "var(--co-inverse)",
          padding: "5px 8px",
          fontFamily: "var(--font-body)",
          fontSize: 12,
          fontWeight: 500,
          color: "var(--co-on-inverse)",
          boxShadow: "0 4px 12px var(--co-panel-shadow)",
        }}
      >
        {tip.text}
      </span>,
      portalContainer,
    );

  return { bindTip, tipPortal };
}
